<script setup>
import { onMounted, ref } from "vue";
import fetchData from "@/utils/fetchData";
import { buildIcs, googleCalendarLink } from "@/utils/calendar";

// The dated events in a make, ready to add to a calendar
const props = defineProps({
  text: { type: String, required: true },
  title: { type: String, default: "" },
});
defineEmits(["close"]);

const events = ref([]);
const loading = ref(true);
const error = ref("");

const today = () => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
    now.getDate()
  )}`;
};

const find = async () => {
  loading.value = true;
  error.value = "";
  const { response } = await fetchData("generate/events", {
    text: props.text,
    today: today(),
  });
  if (response.status === 201 && Array.isArray(response.result?.events)) {
    events.value = response.result.events;
  } else if (response.status === 401) {
    error.value = "Please log in to add this to a calendar";
  } else {
    error.value = "We couldn't read the dates, please try again";
  }
  loading.value = false;
};
onMounted(find);

const describe = (event) => {
  const [date, time] = event.start.split("T");
  const day = new Date(`${date}T12:00:00`).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return time ? `${day}, ${time}` : day;
};

const downloadAll = () => {
  const blob = new Blob([buildIcs(events.value)], {
    type: "text/calendar;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${(props.title || "Events")
    .replace(/[\\/:*?"<>|]+/g, "")
    .slice(0, 60)}.ics`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
};
</script>

<template>
  <section
    class="mt-4 rounded-lg border border-gray-200 bg-gray-10 p-4 text-primary"
    aria-labelledby="calendar-title"
  >
    <div class="mb-2 flex items-center justify-between">
      <p id="calendar-title" class="font-bold">Add to your calendar</p>
      <button
        type="button"
        class="text-secondary hover:text-primary"
        title="Close"
        @click="$emit('close')"
      >
        <font-awesome-icon :icon="['fas', 'xmark']" />
      </button>
    </div>
    <p v-if="loading" class="text-sm text-secondary" role="status">
      Finding the dates...
    </p>
    <div v-else-if="error" class="text-sm">
      <p class="text-red-500" role="alert">{{ error }}</p>
      <button
        type="button"
        class="mt-1 font-bold uppercase text-accentPrimary"
        @click="find"
      >
        Try again
      </button>
    </div>
    <p v-else-if="!events.length" class="text-sm text-secondary">
      There aren't any dates in this make to add.
    </p>
    <template v-else>
      <ul class="mb-3 divide-y divide-gray-200">
        <li
          v-for="(event, index) in events"
          :key="index"
          class="flex flex-wrap items-center justify-between gap-2 py-2"
        >
          <div class="min-w-0">
            <p class="font-bold [overflow-wrap:anywhere]">{{ event.title }}</p>
            <p class="text-sm text-secondary">
              {{ describe(event)
              }}<span v-if="event.location"> · {{ event.location }}</span>
            </p>
          </div>
          <a
            :href="googleCalendarLink(event)"
            target="_blank"
            rel="noopener noreferrer"
            class="whitespace-nowrap text-sm font-bold text-accentPrimary underline hover:text-purple-400"
            >Google Calendar</a
          >
        </li>
      </ul>
      <button
        type="button"
        class="rounded-lg bg-accentPrimary px-4 py-2 text-sm font-bold uppercase text-white hover:bg-purple-400"
        @click="downloadAll"
      >
        <font-awesome-icon :icon="['fas', 'download']" class="mr-2" />
        {{
          events.length === 1
            ? "Download calendar file"
            : `Download all ${events.length}`
        }}
      </button>
      <p class="mt-2 text-xs text-secondary">
        The file works with Apple Calendar, Outlook and most calendar apps.
      </p>
    </template>
  </section>
</template>
